"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  OPEN_BANNER_EVENT,
  applyConsent,
  getConsent,
  setConsent,
} from "@/lib/consent";

export default function CookieBanner() {
  const pathname = usePathname();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleOpen = () => setVisible(true);
    window.addEventListener(OPEN_BANNER_EVENT, handleOpen);
    return () => window.removeEventListener(OPEN_BANNER_EVENT, handleOpen);
  }, []);

  useEffect(() => {
    if (pathname !== "/") return;
    if (getConsent() !== null) return;

    let observer: IntersectionObserver | null = null;
    let cancelled = false;
    let attempts = 0;

    const attach = () => {
      if (cancelled) return;
      const footer = document.querySelector("footer");
      if (!footer) {
        if (attempts++ < 120) requestAnimationFrame(attach);
        return;
      }
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            setVisible(true);
            observer?.disconnect();
          }
        },
        { threshold: 0.1 },
      );
      observer.observe(footer);
    };

    attach();

    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, [pathname]);

  const decide = (decision: "accepted" | "rejected") => {
    setConsent(decision);
    applyConsent(decision);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          role="dialog"
          aria-label="Cookie consent"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="fixed bottom-4 left-4 z-50 w-[calc(100%-2rem)] max-w-[640px] rounded-2xl border border-white/10 bg-[#091661] p-6 text-white shadow-2xl sm:bottom-6 sm:left-6 sm:p-8"
        >
          <p className="mb-6 max-w-[560px] text-lg font-medium leading-7 text-[#aeb3ce] sm:text-2xl sm:leading-9">
            We only collect analytics cookies so we can improve your experience.
          </p>
          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
            <Link
              href="/privacy-policy"
              className="mr-auto text-lg font-bold text-white underline decoration-white/40 underline-offset-4 transition-colors hover:text-[#fab49b] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f89470] focus-visible:ring-offset-2 focus-visible:ring-offset-[#091661] sm:text-xl"
            >
              Privacy policy
            </Link>
            <Button
              variant="outline"
              className="h-12 rounded-xl border-white/20 bg-white/10 px-6 text-base font-bold text-white hover:bg-white/20 hover:text-white focus-visible:ring-[#f89470] sm:h-14 sm:px-8 sm:text-lg"
              onClick={() => decide("rejected")}
            >
              Opt out
            </Button>
            <Button
              className="h-12 rounded-xl bg-[#f89470] px-6 text-base font-bold text-[#091661] hover:bg-[#dc6c44] hover:text-[#091661] focus-visible:ring-[#f89470] sm:h-14 sm:px-8 sm:text-lg"
              onClick={() => decide("accepted")}
            >
              Accept
            </Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
